import { Frame, RadixTooltip } from '@timelyapp/react';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { getUser } from 'redux/selectors/user';
import { getUserEntities } from 'redux/selectors/users';
import { useCurrentThemeIsDark } from 'utils/theme';
import styles from './AgentTrackingSessions.module.scss';
import type { AgentProvider } from './agentTracking.model';
import claudeIcon from './images/claude.svg';
import openaiIcon from './images/openai.svg';
import openaiDarkIcon from './images/openai-white.svg';

interface ProviderArtwork {
  light: string;
  /** Official variant for dark backgrounds; marks that work on both omit it. */
  dark?: string;
}
const OPENAI: ProviderArtwork = { light: openaiIcon, dark: openaiDarkIcon };
// Anthropic's dark-background logos keep the Spark in Clay, so one mark serves both themes.
const CLAUDE: ProviderArtwork = { light: claudeIcon };

// Icons only for ids we have artwork for; other providers show their name alone.
const PROVIDER_ICONS: Partial<Record<AgentProvider, ProviderArtwork>> = {
  claude_code: CLAUDE,
  claude_cowork: CLAUDE,
  codex: OPENAI,
  chatgpt: OPENAI,
};

/** Use the backend provider id for artwork and its display name for accessible text. */
export function ProviderIcon({
  provider,
  name,
  selectable = false,
  className,
}: {
  provider: AgentProvider;
  name: string;
  selectable?: boolean;
  className?: string;
}) {
  const dark = useCurrentThemeIsDark();
  const artwork = PROVIDER_ICONS[provider];
  const icon = artwork && ((dark && artwork.dark) || artwork.light);
  return (
    <RadixTooltip tooltip={name} side="top">
      <span
        className={classNames(styles.identityTrigger, className)}
        tabIndex={selectable ? -1 : 0}
        aria-label={`Provider: ${name}`}
      >
        {icon ? (
          <img src={icon} alt={name} width={20} height={20} className={styles.providerIcon} />
        ) : (
          <span className={styles.providerFallback}>{name}</span>
        )}
      </span>
    </RadixTooltip>
  );
}

export function ConductorAvatar({
  userId,
  showName = false,
}: {
  userId: number;
  showName?: boolean;
}) {
  const users = useSelector(getUserEntities);
  const viewer = useSelector(getUser);
  const owner = users[userId] ?? (viewer.id === userId ? viewer : undefined);
  const name = owner?.name || `User ${userId}`;
  const avatarSrc = owner?.avatar?.medium_retina;
  const [failedSrc, setFailedSrc] = useState<string>();
  return (
    <RadixTooltip tooltip={name} side="top">
      <span
        className={classNames(styles.conductorAvatar, styles.identityTrigger)}
        tabIndex={0}
        role="img"
        aria-label={`Conductor: ${name}`}
      >
        <Frame.Avatar
          src={failedSrc === avatarSrc ? undefined : avatarSrc}
          onError={() => setFailedSrc(avatarSrc)}
          alt={owner?.name || '?'}
          size="xsmall"
          shape="circle"
        />
        {showName && (
          <span className={styles.conductorName} aria-hidden="true">
            {owner?.name?.trim().split(/\s+/)[0] || name}
          </span>
        )}
      </span>
    </RadixTooltip>
  );
}
